import { cx } from '@emotion/css';
import {
  arrow as floatingArrow,
  useFloating,
  autoUpdate,
  offset as offsetWare,
  flip,
  shift,
  hide,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingArrow,
  FloatingPortal,
  useMergeRefs,
  safePolygon,
  useId,
  useTransitionStyles,
  useDelayGroup,
  FloatingNode,
  useFloatingNodeId,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import type { FC, HTMLProps, ReactElement, ReactNode, Ref, RefObject } from 'react';
import { cloneElement, forwardRef, isValidElement, useRef, useState } from 'react';
import ODSGroup from '../ODSGroup';
import ODSBadge from '../ODSBadge';

interface TooltipProps extends Omit<HTMLProps<HTMLElement>, 'title' | 'popover'> {
  /** What the tooltip says */
  message?: ReactNode;
  /** Start the tooltip in the open state */
  initialOpen?: boolean;
  /** Where it opens: top, bottom, left, right */
  placement?: Placement;
  /** Should it be displayed? */
  open?: boolean;
  /** If you want to control the open state */
  onOpenChange?: (open: boolean) => void;
  /** Should it include a triangular arrow pointing to the target? */
  arrow?: boolean;
  /** Want it to be another color than black, assign one */
  color?:
    | 'black'
    | 'white'
    | 'success'
    | 'danger'
    | 'warning'
    | 'focus'
    | 'synced'
    | 'primary'
    | 'info';
  /** Need it to be wider or smaller, pass in the size */
  maxWidth?: string;
  /** Do you want this thing to be hoverable after it opens */
  hoverable?: boolean;
  /** Display it as a popover instead */
  popover?: boolean;
  /** Add a title to the tooltip */
  title?: ReactNode;
  /** How far away should it open from the target */
  offset?: number;
  className?: string;
  /** Allow tooltip to break out of parent's container when overflow is set to hidden or scroll */
  ignoreOverflow?: boolean;
  /** Delay in milliseconds before the tooltip opens */
  delay?: number;
  /** Pass in a keyboard shortcut */
  shortcut?: ReactNode;
}

const FLOATING_PORTAL_ID = 'tooltip-floating-portal';

const ODSTooltip = forwardRef<HTMLElement, TooltipProps>(
  (
    {
      message,
      children,
      initialOpen = false,
      placement = 'top',
      open: controlledOpen,
      onOpenChange: setControlledOpen,
      maxWidth = '12rem',
      arrow = false,
      color = 'black',
      className,
      hoverable = false,
      popover = false,
      title,
      offset = 4,
      ignoreOverflow,
      delay = 400,
      shortcut,
    },
    propRef
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const nodeId = useFloatingNodeId();

    const hideOnReferenceHidden = {
      name: 'hideOnReferenceHidden',
      async fn(state) {
        const referenceHidden = state.middlewareData.hide?.referenceHidden;
        const escaped = state.middlewareData.hide?.escaped;
        const { floating } = state.elements;

        if (referenceHidden || (escaped && !ignoreOverflow)) {
          floating.style.visibility = 'hidden';
        } else {
          floating.style.visibility = 'visible';
        }

        return {};
      },
    };

    const arrowRef = useRef<SVGSVGElement>(null);
    const { context, floatingStyles, refs } = useFloating({
      nodeId,
      placement,
      open,
      onOpenChange: setOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
        offsetWare(offset),
        flip({
          crossAxis: placement.includes('-'),
          fallbackAxisSideDirection: 'start',
          altBoundary: !ignoreOverflow,
          padding: 8,
        }),
        shift({ padding: 8 }),
        floatingArrow({ element: arrowRef, padding: 8 }),
        hide({ strategy: 'escaped' }),
        hide({ strategy: 'referenceHidden' }),
        hideOnReferenceHidden,
      ],
    });

    const id = useId();
    const { currentId, isInstantPhase, delay: groupDelay } = useDelayGroup(context, { id });

    const hover = useHover(context, {
      move: false,
      enabled: controlledOpen == null,
      restMs: 100,
      delay: groupDelay || { open: delay, close: 75 },
      ...(hoverable && { handleClose: safePolygon({ blockPointerEvents: false }) }),
    });
    const focus = useFocus(context, { enabled: controlledOpen == null });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
    const instantDuration = 0;
    const duration = 200;
    const childrenRef = (children as ReactElement<{ ref?: RefObject<unknown> }>)?.props?.ref;
    const ref = useMergeRefs([refs.setReference, childrenRef, propRef]);

    const { styles } = useTransitionStyles(context, {
      duration: isInstantPhase
        ? {
            open: instantDuration,
            close: currentId === id ? duration : instantDuration,
          }
        : duration,
      initial: ({ side }) => {
        const startPos = side === 'top' || side === 'left' ? 4 : -4;
        return {
          opacity: 0,
          translate: side === 'top' || side === 'bottom' ? `0 ${startPos}px` : `${startPos}px 0`,
          boxShadow: 'none',
        };
      },
    });

    const trigger = isValidElement<{ ref?: Ref<Element> }>(children) ? (
      cloneElement(children, {
        ref,
        ...getReferenceProps({
          ...(children.props as {}),
        }),
      })
    ) : (
      <span ref={ref} {...getReferenceProps()}>
        {children}
      </span>
    );

    let content = (
      <span css={{ maxWidth }} className="d-inline-block">
        {title !== undefined && (
          <div className="little-heading mb-1" css={{ color: 'inherit !important' }}>
            {title}
          </div>
        )}
        {message}
      </span>
    );

    if (shortcut) {
      content = (
        <ODSGroup verticalAlign="center">
          <span css={{ maxWidth }} className="d-inline-block">
            {content}
          </span>
          <ODSBadge transparent onDark size="xs" className="me-n1">
            {shortcut}
          </ODSBadge>
        </ODSGroup>
      );
    }

    return (
      <>
        {trigger}
        <FloatingNode id={nodeId}>
          {open &&
            (isValidElement(message) ||
              (typeof message === 'string' && message?.trim() !== '')) && (
              /**
               * By default, FloatingPortal will create a new div at the end of the document body.
               * If you need to control the portal outlet, you can use ODSTooltipPortalOutlet.
               * https://floating-ui.com/docs/floatingportal#id
               */
              <FloatingPortal id={FLOATING_PORTAL_ID}>
                <div
                  ref={refs.setFloating}
                  style={{ ...floatingStyles, ...styles }}
                  {...getFloatingProps()}
                  className={cx(
                    'd-print-none',
                    className,
                    !popover
                      ? `ods-tooltip tooltip-${color}`
                      : 'ods-popover popover p-2 text-secondary show',
                    { 'pe-none': !hoverable }
                  )}
                >
                  {content}
                  {arrow && !popover && (
                    <FloatingArrow
                      ref={arrowRef}
                      className={`text-${color}`}
                      context={context}
                      fill="currentColor"
                      width={10}
                      height={5}
                    />
                  )}
                </div>
              </FloatingPortal>
            )}
        </FloatingNode>
      </>
    );
  }
);

export default ODSTooltip;

/**
 * Using this component will create the FloatingPortal tooltip portal outlet in your desired location.
 * https://floating-ui.com/docs/floatingportal#id
 */
export const ODSTooltipPortalOutlet: FC = () => <div id={FLOATING_PORTAL_ID} />;
